import React, {Fragment, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import I18n from 'i18n-js'
import yup from '../../../lib/YupValidations'
import {Formik, ErrorMessage} from 'formik'
import {
  StyledButtonWrapper,
  StyledErrorMessage,
  StyledField,
  StyledForm,
  StyledFloatLabel,
  StyledFragment,
  StyledErrorImage,
  StyledErrorImageWrapper,
  StyledErrorTitle
} from './Form.styled';
import PhoneNumber from './PhoneNumber';
import Password from './Password';
import '../../../../../assets/svgs/error.svg'


const Form = props => {
  const {
    children, fields, onSubmit, className, innerRef,
    isWidget, errorMessage, emailError, phoneError, ...rest
  } = props;
  let validationSchema = yup.object().shape(
    fields.reduce((obj, item) => {
      if (item.validation) {
        item.validation._label = item.label || I18n.t('validations.thisField')
        obj[item.name] = item.validation
      }
      return obj
    }, {}),
  );

  const [emailCustomErrorState, setCustomEmailError] = useState(false);

  useEffect(() => {
    setCustomEmailError(emailError);
  }, [emailError]);

  const initialValues = fields.reduce((obj, item) => {
    obj[item.name] = item.initialValue ? item.initialValue : '';
    return obj
  }, {});

  const emailRule = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  const passwordRule = /^(?=.*[a-z])(?=.*[A-Z])[a-zA-Z\d]{6,}$/;

  const handleEmailBlur = (setFieldTouched, setFieldError, name, value) => {
    if (!value || value === '') {
      setFieldTouched(name, false, true);
      setFieldError(name, null);
    } else if (!emailRule.test(value)) {
      setFieldTouched(name, true, false);
      setFieldError(name, I18n.t('errors.emailFieldError'));
    }
  };

  const handleEmailChange = (setFieldValue, setFieldError, name, value) => {
    setFieldValue(name, value, false);
    setFieldError(name, null);
    setCustomEmailError(false);
  };

  const handlePasswordBlur = (setFieldTouched, setFieldError, name, value, customValidation) => {
    if (!value || value === '') {
      setFieldTouched(name, false, true);
      setFieldError(name, null);
    } else if (!passwordRule.test(value) && customValidation) {
      setFieldTouched(name, true, false);
      setFieldError(name, I18n.t('errors.passwordFieldError'));
    }
  };


  return (
    <Formik initialValues={initialValues}
            innerRef={innerRef} onSubmit={values => {
      onSubmit(values)
    }}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={validationSchema}
    >
      {({errors, touched, values, setFieldTouched, setFieldError, setFieldValue}) => (
        <StyledForm className={className} noValidate>
          {fields.map(field => (
            <Fragment key={field.label}>
              <StyledFragment isFirstInRow={field.isFirstInRow} isHidden={field.isHidden}
                              isLastInRow={field.isLastInRow}
                              isWidget={isWidget}
                              size={field.size}>
                {field.component === 'select' && (
                  <StyledField
                    component="select"
                    id={field.id}
                    name={field.name}
                    validate={field.validate}
                    value={field.value}
                    {...rest}
                  >
                    {field.options.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.name}
                      </option>
                    ))}
                  </StyledField>
                )}
                {field.component === 'input' && (
                  <>
                    <StyledField
                      component={field.component}
                      error={touched[field.name] && errors[field.name]}
                      id={field.id}
                      name={field.name}
                      onBlur={() => {
                        setFieldError(field.name, null);
                      }}
                      onChange={(e) => {
                        setFieldValue(field.name, e.target.value, false);
                        setFieldError(field.name, null);
                      }}
                      type={field.type}
                      validate={field.validate}
                      {...rest}
                    />
                    <StyledFloatLabel
                      error={touched[field.name] && errors[field.name]}
                      hasvalue={values[field.name].length > 0}
                      htmlFor={field.id}
                      {...rest}
                    >
                      {field.label}
                    </StyledFloatLabel>
                  </>
                )}
                {field.id === 'email' && (
                  <>
                    <StyledField
                      component={field.component}
                      customServerError={emailCustomErrorState}
                      error={(touched[field.name] && errors[field.name])}
                      id={field.id}
                      name={field.name}
                      onBlur={(e) => handleEmailBlur(setFieldTouched, setFieldError, field.name, e.target.value)}
                      onChange={(e) =>
                        handleEmailChange(setFieldValue, setFieldError, field.name, e.target.value)
                      }
                      type={field.type}
                      {...rest}
                    />
                    <StyledFloatLabel
                      error={(touched[field.name] && errors[field.name]) || emailCustomErrorState}
                      hasvalue={values[field.name].length > 0}
                      htmlFor={field.id}
                      {...rest}
                    >
                      {field.label}
                    </StyledFloatLabel>
                    {emailCustomErrorState && (<StyledErrorImage>
                        <svg height='20' width='20'>
                          <use xlinkHref="#error"/>
                        </svg>
                      </StyledErrorImage>
                    )}
                  </>
                )}
                {field.id === 'phoneNumber' && (
                  <Fragment>
                    <PhoneNumber
                      error={(touched[field.name] && errors[field.name])}
                      field={field}
                      phoneError={phoneError}
                    />
                  </Fragment>
                )}
                {field.type === 'password' && (
                  <Fragment>
                    <Password
                      error={touched[field.name] && errors[field.name]}
                      field={field}
                      hasvalue={values[field.name].length > 0}
                      helper={field.passwordHelper}
                      onBlur={(e) =>
                        handlePasswordBlur(
                          setFieldTouched,
                          setFieldError,
                          field.name,
                          e.target.value,
                          field.customValidation)
                      }
                      onChange={(e) => {
                        setFieldValue(field.name, e.target.value, false);
                        setFieldError(field.name, null);
                      }}
                      onFocus={() => {
                        setFieldError(field.name, null);
                      }}
                      {...rest}
                    />
                  </Fragment>
                )}
                <ErrorMessage name={field.name}>
                  {msg => <StyledErrorMessage>
                    <StyledErrorImageWrapper>
                      <svg height='16' width='16'>
                        <use xlinkHref="#error"/>
                      </svg>
                    </StyledErrorImageWrapper>
                    <StyledErrorTitle>
                      {msg}
                    </StyledErrorTitle>
                  </StyledErrorMessage>}
                </ErrorMessage>
              </StyledFragment>
            </Fragment>
          ))}
          <StyledButtonWrapper>{children}</StyledButtonWrapper>
        </StyledForm>
      )}
    </Formik>
  )
};

Form.propTypes = {
  children: PropTypes.any,
  className: PropTypes.any,
  emailError: PropTypes.bool,
  enableReinitialize: PropTypes.bool,
  errorMessage: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      component: PropTypes.oneOf(['input', 'select', 'textarea']),
      options: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          value: PropTypes.any.isRequired,
        }),
      ),
      customValidation: PropTypes.bool,
      passwordHelper: PropTypes.bool,
      placeholder: PropTypes.string,
    }),
  ).isRequired,
  initialValues: PropTypes.any,
  innerRef: PropTypes.any,
  isWidget: PropTypes.bool,
  onSubmit: PropTypes.func,
  passwordHelper: PropTypes.bool,
  phoneError: PropTypes.bool
};

export default Form
